import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm, useWatch, type Control } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';
import { useCursorSelect, type CursorPage } from '@/hooks/use-cursor-select';
import { appTzLabel, fromDate, isAfterAppToday, toDayjs } from '@/lib/date';

import { listOrderCustomers } from '../api';
import { toDefaults, toFilters } from '../filters';
import { ORDER_STATUS_CHIP } from '../format';
import { orderKeys, useOrderCities } from '../hooks';
import { orderFiltersSchema } from '../schemas';
import type {
  OrderCustomerOption,
  OrderFilterFormValues,
  OrderListFilters,
  OrderStatus,
} from '../types';

import styles from './order-filter-drawer.module.scss';

const ALL_STATUSES = Object.keys(ORDER_STATUS_CHIP) as OrderStatus[];

const toStatusOption = (value: OrderStatus) => ({ value, label: ORDER_STATUS_CHIP[value].label });

const customerToOption = (c: OrderCustomerOption) => ({ value: c.id, label: c.name });

/** A cursor-paged, server-searched City source (dealer portal). Unset → the admin flat list. */
export type OrderFilterCitySource = {
  queryKey: (search: string) => readonly unknown[];
  fetchPage: (p: { search: string; cursor?: string; limit: number }) => Promise<CursorPage<string>>;
};

const cityToOption = (c: string) => ({ value: c, label: c });

export type OrderFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: OrderListFilters;
  onApply: (filters: OrderListFilters) => void;
  onClose: () => void;
  /** Show the Customers field. Default true; the dealer portal hides it (the Buyer is always the Dealer). */
  showCustomer?: boolean;
  /** Cursor-paged City source. Unset → the admin flat city list. */
  citySource?: OrderFilterCitySource;
  /** Status options in display order. Default: every order status. */
  statusOptions?: readonly OrderStatus[];
};

type BodyProps = Omit<OrderFilterDrawerProps, 'open'>;

type FieldProps = { control: Control<OrderFilterFormValues> };

function CustomerField({ control }: FieldProps) {
  // Cursor-paginated, server-searched typeahead: scroll the popup to load more; a new search
  // term resets to the first page (see `useCursorSelect`). Cursor stays opaque to the FE.
  const customerSelect = useCursorSelect({
    queryKey: orderKeys.metaCustomers,
    fetchPage: async ({ search, cursor, limit }) => {
      const page = await listOrderCustomers({ search, cursor, limit });
      return {
        data: page.data,
        nextCursor: page.meta.nextCursor,
        hasNextPage: page.meta.hasNextPage,
      };
    },
    toOption: customerToOption,
  });

  return (
    <MultiSelectField
      name="customerIds"
      control={control}
      label="Customers"
      placeholder="Select"
      showSearch={customerSelect.showSearch}
      loading={customerSelect.loading}
      onPopupScroll={customerSelect.onPopupScroll}
      options={customerSelect.options}
    />
  );
}

function AdminCityField({ control }: FieldProps) {
  const cities = useOrderCities();
  const cityOptions = useMemo(() => (cities.data ?? []).map(cityToOption), [cities.data]);

  return (
    <MultiSelectField
      name="cities"
      control={control}
      label="City"
      placeholder="Select"
      options={cityOptions}
    />
  );
}

function CursorCityField({ control, source }: FieldProps & { source: OrderFilterCitySource }) {
  const citySelect = useCursorSelect({
    queryKey: source.queryKey,
    fetchPage: source.fetchPage,
    toOption: cityToOption,
  });

  return (
    <MultiSelectField
      name="cities"
      control={control}
      label="City"
      placeholder="Select"
      showSearch={citySelect.showSearch}
      loading={citySelect.loading}
      onPopupScroll={citySelect.onPopupScroll}
      options={citySelect.options}
    />
  );
}

function FilterBody({
  value,
  onApply,
  onClose,
  showCustomer = true,
  citySource,
  statusOptions,
}: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<OrderFilterFormValues>({
    resolver: zodResolver(orderFiltersSchema),
    defaultValues: {
      ...draft,
      orderedFrom: draft.orderedFrom ?? null,
      orderedTo: draft.orderedTo ?? null,
    },
    mode: 'onBlur',
  });

  const statusItems = useMemo(
    () => (statusOptions ?? ALL_STATUSES).map(toStatusOption),
    [statusOptions],
  );

  // Single range control bound to the two draft fields — keeps the two-field wire contract and
  // the From<=To validation on orderedFrom/orderedTo while Figma 12:211846 draws one control.
  const [orderedFrom, orderedTo] = useWatch({
    control: form.control,
    name: ['orderedFrom', 'orderedTo'],
  });
  const orderedError = form.formState.errors.orderedTo?.message;

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      {showCustomer ? <CustomerField control={form.control} /> : null}

      {citySource ? (
        <CursorCityField control={form.control} source={citySource} />
      ) : (
        <AdminCityField control={form.control} />
      )}

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Order Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Order Date Range')}
          format="MM/DD/YYYY"
          disabledDate={isAfterAppToday}
          style={{ width: '100%' }}
          status={orderedError ? 'error' : undefined}
          // One-sided ranges stay supported: the schema, the wire contract (`orderedFrom` /
          // `orderedTo` are independent params) and AC §5's two-independently-clickable-pickers all
          // allow From-only or To-only. `allowEmpty` lets the control confirm a half-filled range,
          // and the value/onChange map each end separately — the shared `toDayjsRange` /
          // `fromDateRange` helpers collapse a partial range to `null`, which is what previously
          // made a one-sided filter impossible to set *or* redisplay (AC §12).
          allowEmpty={[true, true]}
          value={[toDayjs(orderedFrom) ?? null, toDayjs(orderedTo) ?? null]}
          onChange={(range) => {
            form.setValue('orderedFrom', fromDate(range?.[0]) ?? null, { shouldValidate: true });
            form.setValue('orderedTo', fromDate(range?.[1]) ?? null, { shouldValidate: true });
          }}
        />
        {orderedError ? <p className={styles.error}>{orderedError}</p> : null}
      </fieldset>

      <MultiSelectField
        name="statuses"
        control={form.control}
        label="Order Status"
        placeholder="Select"
        options={statusItems}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Order Amount ($)</legend>
        <div className={styles.row}>
          <NumberField
            name="amountFrom"
            control={form.control}
            aria-label="Order Amount From"
            placeholder="From"
            min={0}
            max={999_999_999.99}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="amountTo"
            control={form.control}
            aria-label="Order Amount To"
            placeholder="To"
            min={0}
            max={999_999_999.99}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>No of Devices</legend>
        <div className={styles.row}>
          <NumberField
            name="deviceCountFrom"
            control={form.control}
            aria-label="No of Devices From"
            placeholder="From"
            min={1}
            max={100_000}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="deviceCountTo"
            control={form.control}
            aria-label="No of Devices To"
            placeholder="To"
            min={1}
            max={100_000}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>
    </Drawer>
  );
}

export function OrderFilterDrawer({ open, ...rest }: OrderFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
